import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Truck, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Onboarding
 * Shown to Google-auth users who still need to fill in their business profile
 * (business_name, cif, phone, role, postal_code, radius). Once submitted, the
 * backend marks profile_complete=true and, if the user picked a seller role,
 * starts a 30-day trial.
 */
const Onboarding = () => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    role: user?.role || '',
    business_name: user?.business_name || '',
    cif: user?.cif || '',
    phone: user?.phone || '',
    postal_code: user?.postal_code || '',
    work_radius_km: user?.work_radius_km || 50
  });

  const submit = async () => {
    if (!form.role) return toast.error('Selecciona si eres comprador o vendedor');
    if (!form.business_name || !form.cif || !form.phone) return toast.error('Completa nombre, CIF y teléfono');
    if (!/^\d{5}$/.test(form.postal_code)) return toast.error('Introduce un código postal español válido (5 dígitos)');
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me`, {
        role: form.role,
        business_name: form.business_name,
        cif: form.cif,
        phone: form.phone,
        postal_code: form.postal_code,
        work_radius_km: parseInt(form.work_radius_km)
      }, { headers: { Authorization: getAuthHeader() } });
      await refreshUser();
      toast.success('¡Perfil completado!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { id: 'restaurant', label: 'Comprador', sub: 'Bar, cafetería, restaurante, tienda…', icon: ShoppingCart, testId: 'onboarding-role-buyer' },
    { id: 'supplier', label: 'Vendedor', sub: 'Distribuidor, proveedor, fabricante…', icon: Truck, testId: 'onboarding-role-seller' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-emerald-600 p-3 rounded-lg">
              <Package size={28} className="text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Completa tu perfil</h1>
          <p className="text-sm text-slate-600 mt-1">Necesitamos unos datos rápidos para activar tu cuenta.</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm space-y-5" data-testid="onboarding-form">
          <div className="space-y-3">
            <Label className="text-sm font-medium">¿Qué tipo de negocio eres? *</Label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((c) => {
                const Icon = c.icon;
                const active = form.role === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={c.testId}
                    onClick={() => setForm({ ...form, role: c.id })}
                    className={`p-3 rounded-lg border-2 text-left transition-all duration-200 ${active ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <Icon size={22} className={active ? 'text-emerald-600' : 'text-slate-400'} />
                    <p className="text-sm font-medium mt-2">{c.label}</p>
                    <p className="text-xs text-slate-500 leading-snug">{c.sub}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Nombre del negocio *</Label>
            <Input data-testid="onboarding-business-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Ej: Distribuciones García" />
          </div>
          <div className="space-y-2">
            <Label>CIF *</Label>
            <Input data-testid="onboarding-cif" value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} placeholder="B12345678" />
          </div>
          <div className="space-y-2">
            <Label>Teléfono *</Label>
            <Input data-testid="onboarding-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+34 600 000 000" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Código Postal *</Label>
              <Input data-testid="onboarding-postal" maxLength={5} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, '') })} placeholder="08001" />
            </div>
            <div className="space-y-2">
              <Label>Radio (km) *</Label>
              <Input data-testid="onboarding-radius" type="number" min={1} max={1000} value={form.work_radius_km} onChange={(e) => setForm({ ...form, work_radius_km: e.target.value })} />
            </div>
          </div>

          <Button
            data-testid="onboarding-submit"
            onClick={submit}
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6"
          >
            {saving ? 'Guardando...' : 'Activar mi cuenta'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
